import { Injectable, inject } from '@angular/core';
import { catchError, combineLatest, map, Observable, of } from 'rxjs';
import { DevicesPayload } from '../../devices/components/devices.models';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { RequestProcessingApiService } from './request-processing-api.service';
import {
  EXGG_COMMAND_TYPES,
  RequestProcessingResponse,
  RequestProcessingTable,
  RetryRequestResponse,
  TwoWayRequestItemListResponseDto
} from './request-processing.types';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { PermissionValue, ResourceValue } from '../../../../services/permissions/permissions.types';
import { TranslateService } from 'src/app/services/translate/translate.service';
import { COMMAND } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.constants';

@Injectable({ providedIn: 'root' })
export class RequestProcessingService {
  private readonly permissionsService = inject(PermissionsService);
  private readonly api = inject(RequestProcessingApiService);
  private readonly tz = inject(TimezoneService);
  private readonly translate = inject(TranslateService);

  private readonly commandTranslationKeyByCommandValue = new Map<string, string>(
    (COMMAND.items ?? []).map(item => [String(item.value ?? ''), String(item.text ?? '')])
  );

  readonly cancelPermission$: Observable<boolean>;
  readonly retryPermission$: Observable<boolean>;
  readonly exggFirmwareUpdatePermission$: Observable<boolean>;

  constructor() {
    this.cancelPermission$ = this.permissionsService.hasRight(
      ResourceValue.TWO_WAY_COMMUNICATION,
      PermissionValue.CANCEL
    );

    this.retryPermission$ = this.permissionsService.hasRight(
      ResourceValue.TWO_WAY_COMMUNICATION,
      PermissionValue.RETRY
    );

    this.exggFirmwareUpdatePermission$ = this.permissionsService.hasRight(
      ResourceValue.TWO_WAY_COMMUNICATION,
      PermissionValue.EXGG_FIRMWARE_UPDATES
    );
  }

  canCancel$(commandType: string | null | undefined): Observable<boolean> {
    return this.withCommandTypePermission$(this.cancelPermission$, commandType);
  }

  canRetry$(commandType: string | null | undefined): Observable<boolean> {
    return this.withCommandTypePermission$(this.retryPermission$, commandType);
  }

  private withCommandTypePermission$(
    actionPermission$: Observable<boolean>,
    commandType: string | null | undefined
  ): Observable<boolean> {
    if (!commandType || !EXGG_COMMAND_TYPES.has(commandType)) {
      return actionPermission$;
    }

    return combineLatest([actionPermission$, this.exggFirmwareUpdatePermission$]).pipe(
      map(([hasActionPermission, hasExggPermission]) => hasActionPermission && hasExggPermission)
    );
  }

  private formatUtcDateTime(value: string | null | undefined): string {
    return value ? this.tz.formatLong(value) : '';
  }

  private translateCommand(commandRaw: string): string {
    const key = this.commandTranslationKeyByCommandValue.get(commandRaw);
    return key ? this.translate.instant(key) : commandRaw;
  }

  getTableData(payload: DevicesPayload): Observable<RequestProcessingTable> {
    const initState: RequestProcessingTable = { data: [], total: 0 };

    return this.api.getRequests(payload).pipe(
      map((response: RequestProcessingResponse) => {
        if (!response) return initState;

        const items = response.items ?? [];
        const tableRows = items.map((item: TwoWayRequestItemListResponseDto) => ({
          requestId: item.requestId,
          deviceId: item.deviceId,
          deviceUID: item.deviceUID,
          parentDeviceUID: item.parentDeviceUID ?? null,
          commDeviceId: item.commDeviceId ?? null,
          serialNumber: item.serialNumber ?? null,
          deviceType: item.deviceType,
          command: this.translateCommand(String(item.command ?? '')),
          commandType: String(item.command ?? ''),
          commandStatus: String(item.commandStatus ?? ''),
          commandDetails: item.commandDetails ?? [],
          scheduledDateTime: this.formatUtcDateTime(item.scheduledDateTime),
          createdOnDateTime: this.formatUtcDateTime(item.createdOnDateTime),
          startDateTime: this.formatUtcDateTime(item.startDateTime),
          completedDateTime: this.formatUtcDateTime(item.completedDateTime),
          actionName: String(item.actionName ?? ''),
          imageName: String(item.imageName ?? ''),
          user: item.user,
          canRetry: item.canRetry ?? null,
          canCancel: item.canCancel ?? null
        }));

        return {
          data: tableRows,
          total: response.totalCount ?? tableRows.length
        };
      }),
      catchError(() => of(initState))
    );
  }

  cancelRequest(requestId: number): Observable<boolean> {
    return this.api.cancelRequest(requestId);
  }

  retryRequest(requestId: number): Observable<RetryRequestResponse> {
    return this.api.retryRequest(requestId);
  }
}
